<template>
  <div class="flex flex-col p-3">
    <!-- Stats -->
    <div class="grid grid-cols-6 gap-4 py-2">
      <div
        class="flex flex-col text-green-600 p-3 stat-count shadow bg-white rounded-md"
      >
        <span class="text-2xl font-bold text-center mx-auto">{{
            store.registrationPending?.totalElements ?? 0
          }}</span>
        <span class="text-sm text-center mx-auto">In Queue</span>
      </div>
      <div
        class="flex flex-col text-red-600 p-3 stat-count shadow bg-white rounded-md"
      >
        <span class="text-2xl font-bold text-center mx-auto">{{
            store.registrationFailed?.totalElements ?? 0
          }}</span>
        <span class="text-sm text-center mx-auto">Failed</span>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="flex items-center gap-3 py-2">
      <input
        type="text"
        v-model="search"
        placeholder="Search by BRN"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-48"
        @keyup.enter="fetch"
      />
      <select
        v-model="statuses"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="fetch"
      >
        <option :value="null">-- Filter by Status --</option>
        <option value="PENDING">Pending</option>
        <option value="Success">Success</option>
        <option value="Sent">Sent</option>
        <option value="In-Progress">In Progress</option>
        <option value="Failed">Failed</option>
      </select>

      <select
        v-model="type"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="fetch"
      >
        <option :value="null">-- Filter by Type --</option>
        <option value="REGISTRY">Registry</option>
        <option value="APPLICATION_FORM">Application Form</option>
        <option value="CERTIFICATE">Certificate</option>
        <option value="BUSINESS_ACCOUNT">Business Account</option>
        <option value="PARTNERSHIP_DEED">Partnership Deed</option>
        <option value="JOINT_VENTURE_AGREEMENT">Joint Venture Agreement</option>
        <option value="FORM_6">Form 6</option>
        <option value="CLOSE_AUTH_BUSINESS_ACCOUNT">Close Auth Business Account</option>
        <option value="UPDATE_AUTH_BUSINESS_ACCOUNT_NAME">Update Auth Business Account Name</option>
        <option value="UPDATE_AUTH_BUSINESS_SUB_TYPE">Update Auth Business Sub Type</option>
        <option value="UPDATE_CERTIFICATE">Update Certificate</option>
        <option value="UPDATE_REGISTRY_SUB_TYPE">Update Registry Sub Type</option>
        <option value="UPDATE_REGISTRY_PARTICULARS">Update Registry Particulars</option>
        <option value="UPDATE_REGISTRY_PARTNERS">Update Registry Partners</option>
        <option value="FILING">Filing</option>
        <option value="BENEFICIAL_OWNERSHIP">Beneficial Ownership</option>
        <option value="BENEFICIAL_OWNERSHIP_CHANGE">Beneficial Ownership Change</option>
        <option value="OFFICIAL_ACCOUNT_FORM">Official Account Form</option>
        <option value="OTHERS">Others</option>
        <option value="FILING_PAYMENT">Filing Payment</option>
        <option value="PAYMENT">Payment</option>
        <option value="STATEMENT_OF_CONTINUITY">Statement of Continuity</option>
        <option value="NON_INDIVIDUAL_REGISTRY">Non Individual Registry</option>
        <option value="REGISTER_WITH_NIR">Register With NIR</option>
        <option value="NIR_NAME_CHANGE">NIR Name Change</option>
        <option value="NIR_SUBTYPE_CHANGE">NIR Subtype Change</option>
        <option value="NIR_COMPLIANCE_UPDATE">NIR Compliance Update</option>
        <option value="NIR_STATUS_UPDATE">NIR Status Update</option>
      </select>

      <input
        type="date"
        v-model="startDate"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        @change="fetch"
      />
      <input
        type="date"
        v-model="endDate"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        @change="fetch"
      />
      <select
        v-model="limit"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="fetch"
      >
        <option :value="12">12</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>BRN</th>
          <th>Type</th>
          <th>Owner</th>
          <th>Retry Count</th>
          <th>Status</th>
          <th>Date</th>
          <th>Last Ran</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td v-for="j in 9" :key="j">
              <div class="h-3 bg-gray-200 rounded"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(action, idx) in stream" :key="idx">
            <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
            <td
              class="font-bold font-mono cursor-pointer hover:underline text-primary"
              @click="searchTerm(action.brn)"
            >
              {{ action.brn }}
            </td>
            <td
              class="italic text-blue-600 cursor-pointer hover:underline"
              @click="searchTerm(action.description)"
            >
              {{ action.description }}
            </td>
            <td>{{ action.owner }}</td>
            <td class="text-center">{{ action.retries }}</td>
            <td>
                <span :class="statusPill(action.status)">{{
                    action.status?.toLowerCase()
                  }}</span>
            </td>
            <td class="text-xs">{{ dateFormat(action.createdAt) }}</td>
            <td class="text-xs">{{ dateFormat(action.updatedAt) }}</td>
            <td>
              <div class="flex flex-wrap gap-1">
                <button
                  v-if="action.status === 'Failed'"
                  class="btn-sm-danger"
                  @click="retry(action)"
                >
                  <i class="fa fa-redo mr-1"></i> Retry
                </button>
                <button
                  v-if="canShowRollback(action)"
                  class="btn-sm-danger"
                  :disabled="rollingBackId === action.id"
                  @click="openRollback(action)"
                >
                  <i
                    class="fa mr-1"
                    :class="rollingBackId === action.id ? 'fa-spinner fa-spin' : 'fa-undo'"
                  ></i>
                  Rollback
                </button>
                <button
                  v-if="isReplaceDocumentVisible(action)"
                  class="btn-sm-primary"
                  :disabled="replacingId === action.id"
                  @click="openReplaceDocument(action)"
                >
                  <i
                    class="fa mr-1"
                    :class="replacingId === action.id ? 'fa-spinner fa-spin' : 'fa-exchange-alt'"
                  ></i>
                  Replace
                </button>
                <button class="btn-sm-primary" @click="view(action)">
                  <i class="fa fa-eye mr-1"></i> View
                </button>
                <button
                  class="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs font-medium rounded disabled:opacity-50"
                  :disabled="runningId === action.id"
                  @click="runNow(action)"
                >
                  <i class="fa fa-play mr-1"></i> Run
                </button>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="!loading && stream.length === 0">
          <td colspan="9" class="text-center py-6 text-gray-400 text-sm">
            No results found
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="pagination.totalPages > 1"
      class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2"
    >
      <p class="text-xs text-gray-500">
        Page
        <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
        <span class="font-semibold text-gray-700">{{
            pagination.totalPages
          }}</span>
      </p>
      <div class="flex gap-2">
        <button
          class="btn-sm-primary"
          @click="previous"
          :disabled="currentPage === 1"
        >
          <i class="fa fa-chevron-left mr-1"></i> Prev
        </button>
        <button
          class="btn-sm-primary"
          @click="next"
          :disabled="currentPage === pagination.totalPages"
        >
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>

    <!-- Detail Modal -->
    <AppModal
      v-model="showDetails"
      size="xl"
      title="Process Details"
      hide-footer
    >
      <div v-if="process" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <!-- <p><b>ID:</b> <span class="font-mono text-xs">{{ process.id }}</span></p> -->
          <p><b>BRN:</b> {{ process.brn }}</p>
          <p v-if="process.trackingNumber">
            <b>Tracking Number:</b> {{ process.trackingNumber }}
          </p>
          <p>
            <b>Type (Description):</b> {{ process.description || process.type }}
          </p>
          <p>
            <b>Status:</b>
            <span :class="statusPill(process.status)">{{
                process.status
              }}</span>
          </p>
          <p v-if="process.owner"><b>Owner:</b> {{ process.owner }}</p>
          <p v-if="process.registrar">
            <b>Registrar:</b> {{ process.registrar }}
          </p>
          <p>
            <b>Retries:</b> {{ process.retries ?? process.retryCount }} /
            {{ process.maxRetries ?? 5 }}
          </p>
          <!-- <p v-if="process.referenceId !== null && process.referenceId !== undefined"><b>Reference ID:</b> {{ process.referenceId }}</p> -->
          <p><b>Created At:</b> {{ dateFormat(process.createdAt) }}</p>
          <p><b>Updated At:</b> {{ dateFormat(process.updatedAt) }}</p>
          <p v-if="process.actionIdToken">
            <b>Action ID Token:</b> {{ process.actionIdToken }}
          </p>
          <p v-if="process.fileName">
            <b>File Name:</b> {{ process.fileName }}
          </p>
          <p v-if="process.fileDesc">
            <b>File Description:</b> {{ process.fileDesc }}
          </p>
        </div>

        <p v-if="process.endPoint">
          <b>Endpoint:</b>
          <code
            class="text-xs bg-green-50 border border-green-200 px-1 rounded"
          >{{ process.endPoint }}</code
          >
        </p>

        <div v-if="process.request">
          <p class="font-bold">Request</p>
          <pre
            class="text-green-700 text-xs bg-green-50 border border-green-200 p-2 rounded overflow-auto max-h-96"
          >{{ parseJsonSafe(process.request) }}</pre
          >
        </div>

        <div v-if="process.response || process.bnrResponse">
          <p class="font-bold">Response</p>
          <p
            class="text-red-700 text-xs bg-red-50 border border-red-200 p-2 rounded whitespace-pre-wrap"
          >
            {{ process.response || process.bnrResponse }}
          </p>
        </div>
      </div>
    </AppModal>
    <!-- Rollback Modal -->
    <AppModal
      v-model="showRollback"
      size="md"
      title="Rollback Registration"
      hide-footer
    >
      <div class="space-y-4 text-sm">
        <p class="text-gray-600">
          This will roll back the approved business registration. Provide a rationale to continue.
        </p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">BRN</p>
            <p class="font-mono text-xs break-all">{{ rollbackRow?.brn || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Tracking number</p>
            <p class="font-mono text-xs break-all">{{ resolveTrackingNo(rollbackRow) || "—" }}</p>
          </div>
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Rationale</label>
          <textarea
            v-model="rollbackRationale"
            rows="4"
            placeholder="Explain why this registration should be rolled back..."
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"
            :disabled="rollingBackId != null"
          ></textarea>
        </div>
        <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            class="px-3 py-2 rounded-sm border border-gray-300 text-gray-700"
            :disabled="rollingBackId != null"
            @click="closeRollback"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn-sm-danger"
            :disabled="rollingBackId != null || !rollbackRationale.trim()"
            @click="submitRollback"
          >
            <i
              class="fa mr-1"
              :class="rollingBackId != null ? 'fa-spinner fa-spin' : 'fa-undo'"
            ></i>
            Confirm Rollback
          </button>
        </div>
      </div>
    </AppModal>
    <!-- Replace Document Modal -->
    <AppModal
      v-model="replaceModalOpen"
      size="md"
      title="Replace Document"
      hide-footer
    >
      <form class="space-y-4 text-sm" @submit.prevent="submitReplaceDocument">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">BRN</p>
            <p class="font-mono text-xs break-all">{{ replaceRow?.brn || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Type</p>
            <p class="break-words">{{ resolveBrnKey(replaceRow) || "—" }}</p>
          </div>
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">File name</label>
          <input
            :value="replaceFileName"
            type="text"
            disabled
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Replacement file</label>
          <input
            ref="replaceFileInput"
            type="file"
            required
            class="mt-1 block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
            :disabled="replacingId != null"
            @change="onReplaceFileChange"
          />
        </div>
        <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            class="px-3 py-2 rounded-sm border border-gray-300 text-gray-700"
            :disabled="replacingId != null"
            @click="closeReplaceDocument"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="replacingId != null || !replaceFile || !replaceFileName"
          >
            <i
              class="fa mr-1"
              :class="replacingId != null ? 'fa-spinner fa-spin' : 'fa-exchange-alt'"
            ></i>
            {{ replacingId != null ? "Replacing..." : "Replace" }}
          </button>
        </div>
      </form>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useAbility } from "@casl/vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { useToast } from "@/composables";
import { useInternalProcessesStore } from "../stores";
import { api } from "@/app/services/api.service";

const store = useInternalProcessesStore();
const { successToast, errorToast, warningToast } = useToast();
const { can } = useAbility();

const canViewMenuItem = (permission: string) => can(permission, "general") === true;

const showDetails = ref(false);
const showRollback = ref(false);
const replaceModalOpen = ref(false);
const runningId = ref<string | null>(null);
const rollingBackId = ref<string | null>(null);
const replacingId = ref<string | null>(null);
const retrying = ref(false);
const loading = ref(false);
const currentPage = ref(1);
const limit = ref(12);
const stream = ref<any[]>([]);
const pagination = ref({ totalElements: 0, totalPages: 0 });
const statuses = ref<string | null>(null);
const type = ref<string | null>(null);
const search = ref("");
const startDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
const process = ref<any>(null);
const rollbackRow = ref<any>(null);
const rollbackRationale = ref("");
const replaceRow = ref<any>(null);
const replaceFileName = ref("");
const replaceFile = ref<File | null>(null);
const replaceFileInput = ref<HTMLInputElement | null>(null);

const REPLACE_DOCUMENT_TYPES = [
  "APPLICATION_FORM",
  "PARTNERSHIP_DEED",
  "JOINT_VENTURE_AGREEMENT",
  "FORM_6",
  "UPDATE_CERTIFICATE",
  "FILING",
];

function statusPill(status: string) {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  if (status === "Sent" || status === "Success")
    return base + "bg-green-50 text-green-700";
  if (status === "Failed") return base + "bg-red-50 text-red-700";
  if (status === "PENDING") return base + "bg-gray-100 text-gray-600";
  return base + "bg-gray-100 text-gray-600";
}

function resolveBrnKey(action: any) {
  if (!action || typeof action !== "object") return null;
  const value = action.brnKey ?? action.description ?? action.type ?? null;
  return value != null && value !== "" ? String(value) : null;
}

function resolveTrackingNo(action: any) {
  if (!action || typeof action !== "object") return null;
  const value = action.trackingNo ?? action.trackingNumber ?? null;
  return value != null && value !== "" ? String(value) : null;
}

function resolveFileName(action: any) {
  if (!action || typeof action !== "object") return null;
  const value = action.fileName ?? action.filename ?? action.file_name ?? null;
  return value != null && value !== "" ? String(value) : null;
}

function resolveRetries(action: any) {
  const value = action?.retries ?? action?.retryCount ?? 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function canShowRollback(action: any) {
  if (!canViewMenuItem("can_roll_back_approved_business")) return false;
  if (!action || action.status !== "Failed") return false;
  if (resolveBrnKey(action) !== "REGISTER_WITH_NIR") return false;
  return resolveRetries(action) >= 5;
}

function isReplaceDocumentVisible(action: any) {
  if (!canViewMenuItem("can_replace_registration_attachment")) return false;
  if (!action) return false;
  const status = String(action.status || "");
  if (status === "Success" || status === "Sent") return false;
  const itemType = resolveBrnKey(action);
  return itemType != null && REPLACE_DOCUMENT_TYPES.includes(itemType);
}

const fetch = () => {
  loading.value = true;
  const options = statuses.value ?? "";
  if (search.value) currentPage.value = 1;
  store.fetchRegistrationProcessesStream({
    statuses: options,
    page: currentPage.value,
    limit: limit.value,
    search: search.value,
    types: type.value ?? undefined,
    startDate: startDate.value,
    endDate: endDate.value
  });
};

const searchTerm = (term: string) => {
  search.value = term;
  fetch();
};
const view = async (details: any) => {
  process.value = details;
  showDetails.value = true;

  try {
    const response = await api().get(
      `/business-registration/sync-items/${details.id}`
    );
    process.value = { ...details, ...response.data?.data };
  } catch (e) {
    console.error("Failed to fetch process details:", e);
  }
};

const parseJsonSafe = (jsonStr: string) => {
  try {
    if (!jsonStr) return jsonStr;
    const parsed = JSON.parse(jsonStr);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonStr;
  }
};

const retry = (details: any) => {
  retrying.value = true;
  store.retryRegistrationProcess({
    brn: details.brn,
    description: details.description
  });
};

const runNow = (action: { id: string }) => {
  runningId.value = action.id;
  store.runRegistrationProcess({ id: action.id });
};

const openRollback = (action: any) => {
  if (!canShowRollback(action)) return;
  const trackingNo = resolveTrackingNo(action);
  if (!trackingNo) {
    errorToast("Error", "Tracking number is missing for this record");
    return;
  }
  rollbackRow.value = action;
  rollbackRationale.value = "";
  showRollback.value = true;
};

const closeRollback = () => {
  if (rollingBackId.value != null) return;
  showRollback.value = false;
  rollbackRow.value = null;
  rollbackRationale.value = "";
};

const submitRollback = async () => {
  const action = rollbackRow.value;
  const trackingNo = resolveTrackingNo(action);
  const rationale = rollbackRationale.value.trim();

  if (!action?.id) {
    errorToast("Error", "Record is missing");
    return;
  }
  if (!trackingNo) {
    errorToast("Error", "Tracking number is required");
    return;
  }
  if (!rationale) {
    warningToast("Validation", "Rationale is required");
    return;
  }

  rollingBackId.value = action.id;
  try {
    await store.rollbackNewRegistration({ trackingNo, rationale });
    successToast("Success", "Registration rollback submitted");
    showRollback.value = false;
    rollbackRow.value = null;
    rollbackRationale.value = "";
    fetch();
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    errorToast(
      "Error",
      err.response?.data?.message || err.message || "Could not roll back registration"
    );
  } finally {
    rollingBackId.value = null;
  }
};

const openReplaceDocument = (action: any) => {
  if (!isReplaceDocumentVisible(action)) return;
  const fileName = resolveFileName(action);
  if (!fileName) {
    errorToast("Error", "File name is missing for this record");
    return;
  }
  replaceRow.value = action;
  replaceFileName.value = fileName;
  replaceFile.value = null;
  if (replaceFileInput.value) replaceFileInput.value.value = "";
  replaceModalOpen.value = true;
};

const closeReplaceDocument = () => {
  if (replacingId.value != null) return;
  replaceModalOpen.value = false;
  replaceRow.value = null;
  replaceFileName.value = "";
  replaceFile.value = null;
  if (replaceFileInput.value) replaceFileInput.value.value = "";
};

const onReplaceFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  replaceFile.value = target.files?.[0] ?? null;
};

const submitReplaceDocument = async () => {
  const action = replaceRow.value;
  const fileName = replaceFileName.value.trim();
  const file = replaceFile.value;

  if (!action?.id) {
    errorToast("Error", "Record is missing");
    return;
  }
  if (!fileName) {
    errorToast("Error", "File name is required");
    return;
  }
  if (!file) {
    warningToast("Validation", "Select a replacement file");
    return;
  }

  replacingId.value = action.id;
  try {
    const response = await store.replaceRegistrationAttachment({ fileName, file });
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Document replaced successfully";
    successToast("Success", message);
    replaceModalOpen.value = false;
    replaceRow.value = null;
    replaceFileName.value = "";
    replaceFile.value = null;
    if (replaceFileInput.value) replaceFileInput.value.value = "";
    fetch();
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    errorToast(
      "Error",
      err.response?.data?.message || err.message || "Could not replace document"
    );
  } finally {
    replacingId.value = null;
  }
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};
const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

onMounted(() => fetch());

watch(
  () => store.registrationStream,
  (data) => {
    loading.value = false;
    if (data?.content) {
      stream.value = data.content;
      pagination.value = {
        totalPages: data.totalPages,
        totalElements: data.totalElements
      };
    }
  }
);

watch(
  () => store.registrationStatusChange,
  (data) => {
    if (!data) return;
    const wasActive = runningId.value != null || retrying.value;
    runningId.value = null;
    retrying.value = false;
    if (data.status >= 400 || data.error) {
      errorToast("Error", data.message || "Operation failed");
      return;
    }
    if (wasActive) {
      successToast("Success", data.message || "Process completed successfully");
    }
    fetch();
  }
);

watch(
  () => store.registrationStatusError,
  (error) => {
    if (!error || (!runningId.value && !retrying.value)) return;
    runningId.value = null;
    retrying.value = false;
    errorToast("Error", error.message || "Operation failed");
  }
);
</script>
